// §8.8 equipment picker for LiftDetailCard — container-scoped asset picker
// with live inspection status, utilization color bands, and a HARD BLOCK on
// §4.5-blocked gear (tagged out / past due / never inspected). Manual entry
// (no asset) stays fully supported — zero-sticker workflow preserved.
//
// §8.9 rope WLL fallback: ROPE_WLL_CHART is a CONFORMARA PROPOSED DEFAULT —
// never rendered as verified manufacturer data. Unknown WLL = flagged amber,
// utilization INCOMPLETE, never a refusal.
import { assetApi } from '@/utils/api';
import type { Asset } from '@/types';
import type { LiftComputed } from '@/types/planData';

export type EquipmentContainerType = 'TRUCK' | 'TRAILER' | 'CREW' | 'YARD' | 'OTHER';

export const CONTAINER_TYPES: { value: EquipmentContainerType; label: string }[] = [
  { value: 'TRUCK', label: 'Truck' },
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'CREW', label: 'Crew' },
  { value: 'YARD', label: 'Yard' },
  { value: 'OTHER', label: 'Other' },
];

// Rigging-relevant AssetTypes for the fallback (company-wide) list — §8.8
// principle 1: the tracker serves rigging gear, not every wrench. Includes
// both the server enum names and the legacy form values already in use.
export const RIGGING_ASSET_TYPES = [
  'RIGGING_SLING', 'RIGGING_SHACKLE', 'RIGGING_HOIST', 'FALL_PROTECTION', 'OTHER',
  // legacy AssetFormModal type values (pre-enum forms — same gear)
  'SLING', 'ROPE', 'HOIST', 'LANYARD', 'HARNESS',
];

export interface UsabilityVerdict {
  usable: boolean;
  reason: string | null;
}

export type InspectionDisplay = 'current' | 'periodic-due' | 'tagged-out' | 'never-inspected' | 'unknown';

// Conformara proposed default — NOT verified manufacturer data (§8.9 honesty).
// Common synthetic/wire rope WLL ranges by diameter; crews confirm against
// the maker's chart before trusting the value.
export interface RopeWllChartRow {
  diameterIn: string;
  construction: 'braided' | '3-strand' | 'wire' | 'other';
  proposedWllLbs: number;
}

export const ROPE_WLL_CHART: RopeWllChartRow[] = [
  { diameterIn: '3/8', construction: 'braided', proposedWllLbs: 1450 },
  { diameterIn: '3/8', construction: 'wire', proposedWllLbs: 2200 },
  { diameterIn: '1/2', construction: 'braided', proposedWllLbs: 2500 },
  { diameterIn: '1/2', construction: 'wire', proposedWllLbs: 4100 },
  { diameterIn: '5/8', construction: 'braided', proposedWllLbs: 3900 },
  { diameterIn: '5/8', construction: 'wire', proposedWllLbs: 6400 },
  { diameterIn: '3/4', construction: 'braided', proposedWllLbs: 5600 },
  { diameterIn: '3/4', construction: 'wire', proposedWllLbs: 9200 },
];

export const ROPE_WLL_DISCLAIMER =
  'Conformara proposed default — NOT verified manufacturer data. Confirm against the manufacturer tag or spec sheet.';

export const WLL_UNKNOWN_MESSAGE = 'WLL UNKNOWN — verify from manufacturer tag/spec sheet';

export const CUT_ROPE_HINT =
  'Cut-rope practice: tag in heat-shrink at the original reel end? Photograph and attach to this record BEFORE the rope is cut.';

// §8.9: unknown WLL is INCOMPLETE utilization (amber), never a refusal.
export type UtilizationBand = 'ok' | 'amber' | 'red' | 'incomplete' | 'n/a';

export function utilizationBand(forceLbs: number, wllLbs: number | null | undefined): {
  band: UtilizationBand; percent: number | null;
} {
  if (wllLbs == null || wllLbs <= 0) {
    // Unknown WLL: the calculation still runs (geometry doesn't need WLL),
    // but the check is INCOMPLETE — amber, flagged, never guessed.
    return { band: 'incomplete', percent: null };
  }
  if (forceLbs <= 0) return { band: 'n/a', percent: null };
  const percent = (forceLbs / wllLbs) * 100;
  if (percent > 100) return { band: 'red', percent };
  if (percent >= 80) return { band: 'amber', percent };
  return { band: 'ok', percent };
}

export function utilizationColor(band: UtilizationBand): string {
  switch (band) {
    case 'red': return 'text-red-600';
    case 'amber': return 'text-amber-600';
    case 'incomplete': return 'text-amber-600';
    case 'ok': return 'text-green-600';
    default: return 'text-gray-500';
  }
}

// Live inspection status label per §8.8 #3.
export function inspectionDisplay(verdict: UsabilityVerdict | undefined, _asset?: Asset): InspectionDisplay {
  if (!verdict) return 'unknown';
  if (!verdict.usable && /tagged out|out of service/i.test(verdict.reason ?? '')) return 'tagged-out';
  if (!verdict.usable && /never inspected/i.test(verdict.reason ?? '')) return 'never-inspected';
  if (!verdict.usable && /past due|overdue/i.test(verdict.reason ?? '')) return 'periodic-due';
  if (!verdict.usable) return 'tagged-out';
  // Usable ⇒ current (the gate itself distinguishes the other states).
  return 'current';
}

export function inspectionBadge(display: InspectionDisplay): { icon: string; label: string; cls: string } {
  switch (display) {
    case 'current': return { icon: '✓', label: 'Inspection current', cls: 'bg-green-50 text-green-700' };
    case 'periodic-due': return { icon: '⚠', label: 'Periodic due', cls: 'bg-amber-50 text-amber-700' };
    case 'tagged-out': return { icon: '✗', label: 'Tagged out', cls: 'bg-red-50 text-red-700' };
    case 'never-inspected': return { icon: '✗', label: 'Never inspected', cls: 'bg-red-50 text-red-700' };
    default: return { icon: '·', label: 'Inspection unknown', cls: 'bg-gray-50 text-gray-500' };
  }
}

export interface EquipmentPickerProps {
  assets: Asset[];
  verdicts: Record<string, UsabilityVerdict>;
  containers: EquipmentContainerType[];
  onContainersChange: (next: EquipmentContainerType[]) => void;
  role: 'hoist-line' | 'tag-line' | 'static-line' | 'sling' | 'block' | 'shackle';
  wllLbs: string;
  computed: LiftComputed | null;
  onSelect: (assetId: number, role: 'hoist-line' | 'tag-line' | 'static-line' | 'sling' | 'block' | 'shackle', wllLbs: number) => void;
  disabled?: boolean;
}

// Computed force per equipment ROLE from the lift's computed payload.
// Per-component forces only exist for some roles; where the current computed
// shape has no per-component force the lift GROSS is used per PRD (deviation
// noted in the slice report).
export function computedForceForRole(
  role: 'hoist-line' | 'tag-line' | 'static-line' | 'sling' | 'block' | 'shackle',
  computed: LiftComputed | null
): number | null {
  if (!computed || computed.status !== 'ok') return null;
  const f = computed.forces;
  switch (role) {
    case 'hoist-line': return f.linePartTensionLbs;
    case 'tag-line': return f.tagLineLbs ?? null;
    case 'static-line': return f.staticLineTensionLbs ?? null;
    case 'sling': return f.slingLegLbs ?? null;
    case 'block': return f.topBlockResultantLbs;
    case 'shackle': return f.linePartTensionLbs;
    default: return null;
  }
}

export interface ContainerRef {
  refType: 'VEHICLE' | 'TEAM' | 'MARKET' | 'PROJECT';
  refId: number;
}

export interface ContainerCandidate {
  key: string; // 'VEHICLE:10' (pointer) or 'label:Truck 1435' (legacy)
  label: string;
  containerType: string;
  refType?: 'VEHICLE' | 'TEAM' | 'MARKET' | 'PROJECT';
  refId?: number;
}

/**
 * S2-LUNA-DELTA-001 (2026-09-30): the picker's "which specific container"
 * candidates, derived from the loaded asset list — pointer-based containers
 * dedupe by pointer, legacy label-only rows by exact label. Only containers
 * of the checked kinds are offered.
 */
export function distinctContainers(assets: Asset[], kinds: EquipmentContainerType[]): ContainerCandidate[] {
  const seen = new Set<string>();
  const out: ContainerCandidate[] = [];
  for (const a of assets) {
    if (!a.containerType || !kinds.includes(a.containerType as EquipmentContainerType)) continue;
    const any = a as unknown as { containerRefType?: string | null; containerRefId?: number | null };
    if (any.containerRefType && any.containerRefId != null) {
      const key = `${any.containerRefType}:${any.containerRefId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        label: a.containerLabel ?? key,
        containerType: a.containerType,
        refType: any.containerRefType as ContainerRef['refType'],
        refId: any.containerRefId,
      });
    } else if (a.containerLabel) {
      const key = `label:${a.containerLabel}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: a.containerLabel, containerType: a.containerType });
    }
  }
  return out.sort((x, y) => x.label.localeCompare(y.label));
}

export function filterAssetsByContainers(
  assets: Asset[],
  containers: EquipmentContainerType[],
  containerLabels?: string[],
  containerRefs?: ContainerRef[]
): Asset[] {
  if (containers.length === 0) return assets; // no scoping → full company list (fallback)
  // Container-scope semantics (§8.8): when a container scope is ACTIVE, only
  // assets assigned to one of the selected containers are shown. Containerless
  // assets are HIDDEN — that is the intent (the plan header scopes the picker
  // to gear on the declared crew/truck/trailer; unassigned yard gear is not
  // what the scope is for). Clear the checkboxes to see the full list.
  // Luna S2-LUNA-002 fix (Option D): POINTER-FIRST — when the caller knows
  // WHICH containers (persisted refType/refId pointers), a plan on Truck 1435
  // must not see Truck 999's gear. Label match is the LEGACY FALLBACK for
  // rows created before pointers existed; omitted refs+labels keep plain kind
  // semantics (widening filter, e.g. "all trucks").
  const labelSet = containerLabels && containerLabels.length > 0 ? new Set(containerLabels) : null;
  const refSet = containerRefs && containerRefs.length > 0
    ? new Set(containerRefs.map((r) => `${r.refType}:${r.refId}`))
    : null;
  return assets.filter((a) => {
    const inContainer = !!a.containerType && containers.includes(a.containerType as EquipmentContainerType);
    if (!inContainer) return false;
    if (refSet) {
      const any = a as unknown as { containerRefType?: string | null; containerRefId?: number | null };
      if (any.containerRefType && any.containerRefId != null) {
        // Pointer present: pointer decides (pointer wins over label).
        return refSet.has(`${any.containerRefType}:${any.containerRefId}`);
      }
      // Legacy label-only row: exact label fallback, else hidden in a
      // specific-container scope (never silently widened to other trucks).
      return !!a.containerLabel && !!labelSet && labelSet.has(a.containerLabel);
    }
    if (labelSet && !a.containerLabel) return false; // specific scope hides unlabelled gear
    if (labelSet && !labelSet.has(a.containerLabel!)) return false;
    return true;
  });
}

export function isRiggingRelevant(a: Asset): boolean {
  return RIGGING_ASSET_TYPES.includes(a.type);
}

// ---------------------------------------------------------------------------
// UAT-014 (user-POV test 2026-10-06, HIGH): role/type compatibility. The lift
// equipment picker previously offered a flat company-wide list where a PPE
// harness appeared as a selectable lift component. A role chooses which asset
// TYPES may fill it — personal gear (harness/lanyard/fall-protection) is never
// a lift component, so it is never offered.
// ---------------------------------------------------------------------------
export type EquipmentRole =
  | 'hoist-line' | 'tag-line' | 'static-line' | 'sling' | 'block' | 'shackle';

export const ROLE_COMPATIBLE_ASSET_TYPES: Record<EquipmentRole, string[]> = {
  'hoist-line': ['RIGGING_HOIST', 'HOIST', 'ROPE', 'OTHER'],
  'tag-line': ['ROPE', 'OTHER'],
  'static-line': ['ROPE', 'OTHER'],
  sling: ['RIGGING_SLING', 'SLING'],
  block: ['OTHER'],
  shackle: ['RIGGING_SHACKLE'],
};

/** Only role/type-compatible assets are offered for the given role (UAT-014). */
export function assetsForRole(role: EquipmentRole, assets: Asset[]): Asset[] {
  const allowed = ROLE_COMPATIBLE_ASSET_TYPES[role] ?? [];
  return assets.filter((a) => allowed.includes(a.type));
}